/**
 * PropertyDocumentFolder — Dokumentenmappe pro Objekt (P2).
 *
 * Zeigt alle Dokumente rund um ein Objekt in 3 Sektionen:
 *   1. Objekt-Dokumente (direkt am Objekt hinterlegt)
 *   2. Einheiten-Dokumente
 *   3. Mietverhältnis-Dokumente
 *
 * Für jedes Dokument: Download-Button (authenticated), Metadaten (Datum, Kategorie).
 */
import { useEffect, useMemo, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Folder, FileText, MagnifyingGlass, Buildings, House, Users,
  ArrowSquareOut, Download,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso.slice(0, 10); }
}

function fmtSize(bytes) {
  if (!bytes) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

async function downloadDocument(docId, fallbackName) {
  try {
    const r = await fetch(`${API}/documents/${docId}/download`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fallbackName || "document.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    toast.error(`Download fehlgeschlagen: ${e.message}`);
  }
}

function DocRow({ doc }) {
  return (
    <div
      className="grid grid-cols-[auto_1fr_auto_auto_auto] gap-3 items-center px-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
      data-testid={`doc-row-${doc.id}`}
    >
      <FileText size={16} className="text-slate-400" />
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate" title={doc.title || doc.original_filename}>
          {doc.title || doc.original_filename || "—"}
        </div>
        <div className="text-[10px] text-slate-500 truncate">
          {doc.original_filename}
          {doc.author_name ? ` · ${doc.author_name}` : ""}
          {doc.my_reference ? ` · Az. ${doc.my_reference}` : ""}
        </div>
      </div>
      {doc.category && (
        <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200">
          {doc.category}
        </Badge>
      )}
      <div className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
        {fmtDate(doc.created_at)} · {fmtSize(doc.size_bytes)}
      </div>
      <Button
        size="icon" variant="ghost"
        onClick={() => downloadDocument(doc.id, doc.original_filename)}
        className="h-7 w-7 hover:text-emerald-700"
        title="Download"
        data-testid={`doc-download-${doc.id}`}
      >
        <Download size={14} />
      </Button>
    </div>
  );
}

function Section({ icon: Icon, title, count, children, testId }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50/70 border-b border-slate-200 hover:bg-slate-100 transition-colors"
      >
        <Icon size={16} className="text-slate-500" />
        <div className="text-sm font-semibold text-slate-800">{title}</div>
        <Badge className="text-[10px] bg-slate-200 text-slate-700 border-0">{count}</Badge>
        <span className="ml-auto text-slate-400 text-xs">{open ? "▾" : "▸"}</span>
      </button>
      {open && <div className="divide-y divide-slate-100">{children}</div>}
    </section>
  );
}

export default function PropertyDocumentFolder({ propertyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!propertyId) return;
    setLoading(true);
    api.get(`/documents/folder/property/${propertyId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [propertyId]);

  const filtered = useMemo(() => {
    if (!data) return null;
    const f = filter.trim().toLowerCase();
    const matches = (d) => {
      if (!f) return true;
      return (d.title || "").toLowerCase().includes(f)
        || (d.original_filename || "").toLowerCase().includes(f)
        || (d.category || "").toLowerCase().includes(f)
        || (d.my_reference || "").toLowerCase().includes(f);
    };
    return {
      ...data,
      property_docs: data.property_docs.filter(matches),
      unit_docs: data.unit_docs.filter(matches),
      tenancy_docs: data.tenancy_docs.filter(matches),
    };
  }, [data, filter]);

  if (loading) return <div className="text-sm text-slate-500 py-8 text-center">Lade Dokumentenmappe…</div>;
  if (!data) return <div className="text-sm text-slate-500 py-8 text-center">Fehler beim Laden.</div>;

  const isEmpty = data.counts.total === 0;
  return (
    <div className="space-y-4" data-testid="property-document-folder">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-slate-700">
          <Folder size={20} weight="duotone" className="text-emerald-700" />
          <div>
            <div className="text-lg font-semibold text-slate-900">Dokumentenmappe</div>
            <div className="text-xs text-slate-500">
              {data.counts.total} Dokument{data.counts.total === 1 ? "" : "e"} · aktuell für {data.property?.name}
            </div>
          </div>
        </div>
        <div className="ml-auto relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2 text-slate-400" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Suchen…"
            className="pl-8 h-8 w-72 text-sm"
            data-testid="doc-folder-search"
          />
        </div>
      </div>

      {isEmpty ? (
        <div className="rounded-xl border border-dashed border-slate-300 py-12 text-center bg-slate-50">
          <Folder size={44} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-600">Noch keine Dokumente zu diesem Objekt.</div>
        </div>
      ) : (
        <div className="space-y-3">
          <Section
            icon={Buildings} title="Objekt-Dokumente"
            count={filtered.property_docs.length} testId="doc-section-property">
            {filtered.property_docs.length === 0
              ? <div className="text-xs text-slate-400 py-3 px-4 italic">Keine Treffer.</div>
              : filtered.property_docs.map((d) => <DocRow key={d.id} doc={d} />)}
          </Section>
          <Section
            icon={House} title="Einheiten-Dokumente"
            count={filtered.unit_docs.length} testId="doc-section-units">
            {filtered.unit_docs.length === 0
              ? <div className="text-xs text-slate-400 py-3 px-4 italic">Keine Treffer.</div>
              : filtered.unit_docs.map((d) => <DocRow key={d.id} doc={d} />)}
          </Section>
          <Section
            icon={Users} title="Mietverhältnis-Dokumente"
            count={filtered.tenancy_docs.length} testId="doc-section-tenancies">
            {filtered.tenancy_docs.length === 0
              ? <div className="text-xs text-slate-400 py-3 px-4 italic">Keine Treffer.</div>
              : filtered.tenancy_docs.map((d) => <DocRow key={d.id} doc={d} />)}
          </Section>
        </div>
      )}
    </div>
  );
}
